import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { useTaskStore, TaskStatus, TaskPriority } from '../../../store/taskStore';
import { useAuthStore } from '../../../store/authStore';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

export function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentTask, fetchTask, updateTask, deleteTask, isLoading, error,
    comments, activity, fetchComments, addComment, deleteComment, fetchActivity,
    attachments, fetchAttachments, addAttachment, deleteAttachment,
  } = useTaskStore();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [attForm, setAttForm] = useState({ fileName: '', fileUrl: '' });
  const [addingAtt, setAddingAtt] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<{
    title: string;
    description: string;
    status: TaskStatus;
    priority: TaskPriority;
    dueDate: string;
  }>({
    title: '',
    description: '',
    status: 'backlog',
    priority: 'medium',
    dueDate: '',
  });

  useEffect(() => {
    if (id) {
      fetchTask(id);
      fetchComments(id);
      fetchActivity(id);
      fetchAttachments(id);
    }
  }, [id]);

  const handleAddAttachment = async () => {
    if (!id) return;
    if (!attForm.fileName.trim() || !attForm.fileUrl.trim()) { toast.error('File name and link are required'); return; }
    if (!/^https?:\/\/.+/i.test(attForm.fileUrl.trim())) { toast.error('Enter a valid URL (https://…)'); return; }
    setAddingAtt(true);
    try {
      await addAttachment(id, { fileName: attForm.fileName.trim(), fileUrl: attForm.fileUrl.trim() });
      setAttForm({ fileName: '', fileUrl: '' });
      toast.success('Attachment added');
    } catch {
      toast.error('Failed to add attachment');
    } finally {
      setAddingAtt(false);
    }
  };

  const handleAddComment = async () => {
    if (!id || !newComment.trim()) return;
    setPostingComment(true);
    try {
      await addComment(id, newComment.trim());
      setNewComment('');
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!id) return;
    try {
      await deleteComment(id, commentId);
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  const relTime = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return new Date(d).toLocaleDateString();
  };

  const activityLabel = (a: { action: string; from_value?: string; to_value?: string; field?: string }) => {
    switch (a.action) {
      case 'created': return 'created this task';
      case 'status_changed': return `changed status ${a.from_value ?? '?'} → ${a.to_value ?? '?'}`;
      case 'assigned': return `reassigned the task`;
      case 'commented': return 'added a comment';
      case 'attachment_added': return `attached ${a.to_value ?? 'a file'}`;
      default: return a.action.replace(/_/g, ' ');
    }
  };

  useEffect(() => {
    if (currentTask) {
      setEditData({
        title: currentTask.title,
        description: currentTask.description || '',
        status: currentTask.status,
        priority: currentTask.priority,
        dueDate: currentTask.dueDate ? currentTask.dueDate.split('T')[0] : '',
      });
    }
  }, [currentTask]);

  const handleSave = async () => {
    if (!id) return;
    try {
      await updateTask(id, editData);
      toast.success('Task updated successfully');
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update task:', err);
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await deleteTask(id);
      toast.success('Task deleted successfully');
      navigate('/work/tasks');
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading task...</p>
        </div>
      </div>
    );
  }

  if (!currentTask) {
    return (
      <div className="text-center py-12">
        <h1 className="tracking-tight text-foreground mb-4 font-display text-page-title">Task Not Found</h1>
        <Link to="/work/tasks">
          <Button variant="primary">Back to Tasks</Button>
        </Link>
      </div>
    );
  }


  const priorityColors: Record<string, string> = {
    low: 'bg-card text-muted-foreground',
    medium: 'bg-info/20 text-info',
    high: 'bg-warning/20 text-warning',
    urgent: 'bg-destructive/20 text-destructive',
  };

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div className="flex items-center gap-4">
          <Link to="/work/tasks" className="p-2 bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors">
            ←
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-primary">{currentTask.code}</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-xs font-semibold text-muted-foreground">Tasks</span>
            </div>
            <h1 className="tracking-tight text-foreground font-display text-page-title">{currentTask.title}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="primary" onClick={handleSave}>Check In Changes</Button>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button variant="primary" onClick={() => setIsEditing(true)}>Edit Work Item</Button>
              <Button variant="danger" onClick={handleDelete}>Delete</Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-semibold">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-8">
            <h2 className="text-md text-foreground mb-6 flex items-center gap-2 font-display text-section-title">
              <span className="text-primary"></span> Scope & Description
            </h2>
            <div className="space-y-6">
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs text-muted-foreground font-semibold mb-2">Title</label>
                    <Input
                      type="text"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground font-semibold mb-2">Detailed Narrative</label>
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={10}
                      className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm leading-relaxed"
                    />
                  </div>
                </>
              ) : (
                <div className="bg-background/50 p-6 rounded-lg border border-border/50">
                  <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">
                    {currentTask.description || 'No detailed scope provided for this work item.'}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Discussion — real comments */}
          <Card className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-md text-foreground font-display text-section-title">Discussion</h2>
              <span className="text-xs font-semibold text-muted-foreground">{comments.length} comment{comments.length === 1 ? '' : 's'}</span>
            </div>

            <div className="space-y-4 mb-6">
              {comments.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center bg-background/30 rounded-lg border border-dashed border-border/50">
                  No comments yet. Start the discussion below.
                </p>
              ) : comments.map((c) => (
                <div key={c.id} className="flex gap-3">
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-semibold text-primary">
                    {(c.author_id || 'U').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground">
                        {c.author_id === currentUserId ? 'You' : 'Member'}
                      </span>
                      <span className="text-xs text-muted-foreground">{relTime(c.created_at)}</span>
                      {c.author_id === currentUserId && (
                        <button
                          onClick={() => handleDeleteComment(c.id)}
                          className="ml-auto text-xs text-muted-foreground hover:text-destructive"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-foreground mt-1 whitespace-pre-wrap break-words">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                placeholder="Write a comment…"
                className="flex-1"
              />
              <Button variant="primary" onClick={handleAddComment} isLoading={postingComment} disabled={!newComment.trim()}>
                Comment
              </Button>
            </div>
          </Card>

          {/* Activity history */}
          <Card className="p-8">
            <h2 className="text-md text-foreground mb-6 font-display text-section-title">Activity</h2>
            {activity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
            ) : (
              <div className="relative pl-5 border-l-2 border-border space-y-4">
                {activity.map((a) => (
                  <div key={a.id} className="relative">
                    <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-primary/30 border-2 border-primary" />
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{a.actor_id === currentUserId ? 'You' : 'A member'}</span>{' '}
                      {activityLabel(a)}
                    </p>
                    <p className="text-xs text-muted-foreground">{relTime(a.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Attachments — real links */}
          <Card className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-md text-foreground font-display text-section-title">Attachments</h2>
              <span className="text-xs font-semibold text-muted-foreground">{attachments.length} file{attachments.length === 1 ? '' : 's'}</span>
            </div>

            <div className="space-y-2 mb-6">
              {attachments.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center bg-background/30 rounded-lg border border-dashed border-border/50">
                  No attachments yet. Add a link below.
                </p>
              ) : attachments.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/40 px-3 py-2">
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{a.file_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{relTime(a.created_at)}</p>
                  </div>
                  <a
                    href={a.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    Open
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                  </a>
                  <button
                    onClick={async () => {
                      if (!id) return;
                      try { await deleteAttachment(id, a.id); toast.success('Attachment removed'); }
                      catch { toast.error('Failed to remove attachment'); }
                    }}
                    className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={attForm.fileName}
                onChange={(e) => setAttForm((f) => ({ ...f, fileName: e.target.value }))}
                placeholder="File name (e.g. Design spec.pdf)"
                className="flex-1"
              />
              <Input
                value={attForm.fileUrl}
                onChange={(e) => setAttForm((f) => ({ ...f, fileUrl: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAttachment(); } }}
                placeholder="https://…"
                className="flex-1"
              />
              <Button variant="primary" onClick={handleAddAttachment} isLoading={addingAtt} disabled={!attForm.fileName.trim() || !attForm.fileUrl.trim()}>
                Add
              </Button>
            </div>
          </Card>
        </div>

        {/* Info Sidebar */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-muted-foreground mb-6 border-b border-border pb-3 font-display text-card-title">Work Item Properties</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-xs text-muted-foreground font-semibold mb-2">Stage</label>
                {isEditing ? (
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value as any })}
                    className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-semibold"
                  >
                    <option value="backlog">Backlog</option>
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                  </select>
                ) : (
                  <Badge variant={currentTask.status === 'done' ? 'success' : 'default'} className="capitalize border-border px-4">{currentTask.status}</Badge>
                )}
              </div>

              <div>
                <label className="block text-xs text-muted-foreground font-semibold mb-2">Priority</label>
                {isEditing ? (
                  <select
                    value={editData.priority}
                    onChange={(e) => setEditData({ ...editData, priority: e.target.value as any })}
                    className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-semibold"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                ) : (
                  <Badge className={`capitalize border-border px-4 ${priorityColors[currentTask.priority]}`}>{currentTask.priority}</Badge>
                )}
              </div>

              <div className="pt-4 border-t border-border/30">
                <label className="block text-xs text-muted-foreground font-semibold mb-2">Deadline</label>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editData.dueDate}
                    onChange={(e) => setEditData({ ...editData, dueDate: e.target.value })}
                    className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-semibold"
                  />
                ) : (
                  <p className="text-foreground font-semibold text-sm">
                    {currentTask.dueDate ? new Date(currentTask.dueDate).toLocaleDateString(undefined, { dateStyle: 'long' }) : 'Flexible'}
                  </p>
                )}
              </div>

              <div className="pt-4 border-t border-border/30">
                <label className="block text-xs text-muted-foreground font-semibold mb-2">Resource Assigned</label>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary border border-primary/30 flex items-center justify-center text-xs font-semibold text-primary">
                    {currentTask.assigneeName?.[0] || 'U'}
                  </div>
                  <p className="text-foreground font-semibold text-sm">{currentTask.assigneeName || 'Unassigned'}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Timeline Artifacts */}
          <Card className="p-6">
            <h2 className="text-muted-foreground mb-4 font-display text-card-title">Metadata</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center bg-background/40 p-3 rounded-lg border border-border/50">
                <span className="text-xs text-muted-foreground font-semibold">Initiated</span>
                <span className="text-xs font-semibold text-foreground">{new Date(currentTask.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between items-center bg-background/40 p-3 rounded-lg border border-border/50">
                <span className="text-xs text-muted-foreground font-semibold">Last Synchronized</span>
                <span className="text-xs font-semibold text-foreground">{new Date(currentTask.updatedAt).toLocaleDateString()}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
